import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery,
  InputAdornment,
  MenuItem,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";
import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "app/components/status_dialog";

export default function OpsSalesPage() {
  const pageTitle = "Sales Order";
  usePageTitle(pageTitle);

  const { entityRef, ruleRef } = useParams();
  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();
  const { applocation } = useAppLocation();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showLoading, setShowLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [pageLoaded, setPageLoaded] = useState(false);
  const [salesRule, setSalesRule] = useState(null);
  const [entity, setEntity] = useState(null);
  const [products, setProducts] = useState([]);

  // Order state: Array of selected line items
  const [orderItems, setOrderItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [collectPayment, setCollectPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("BANK_TRANSFER");

  // Input search value state to control resetting the autocomplete box
  const [inputValue, setInputValue] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`/agents/ops/sales/${entityRef}/${ruleRef}/init.json`)
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            setSalesRule(result.data.data.sales_rule);
            setEntity(result.data.data.entity);
            setProducts(result.data.data.products || []);
            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message || "Failed to load data.");
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [entityRef, ruleRef]);

  function singularize(word) {
    if (!word || typeof word !== "string") return word;

    const irregular = {
      people: "person",
      men: "man",
      women: "woman",
      children: "child",
      teeth: "tooth",
      feet: "foot",
      mice: "mouse",
      geese: "goose",
      data: "datum",
      indices: "index",
      analyses: "analysis",
      crises: "crisis",
      diagnoses: "diagnosis",
      theses: "thesis",
      lives: "life",
      wives: "wife",
      knives: "knife",
      leaves: "leaf",
      shelves: "shelf",
      wolves: "wolf",
    };

    const lower = word.toLowerCase();

    if (irregular[lower]) {
      return matchCase(word, irregular[lower]);
    }

    if (lower.endsWith("ies") && lower.length > 3) {
      return matchCase(word, lower.slice(0, -3) + "y");
    }

    if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      return matchCase(word, lower.slice(0, -2));
    }

    if (lower.endsWith("ves")) {
      return matchCase(word, lower.slice(0, -3) + "f");
    }

    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return matchCase(word, lower.slice(0, -1));
    }

    return word;
  }

  function matchCase(original, replacement) {
    return original[0] === original[0].toUpperCase()
      ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
      : replacement;
  }

  // Base URL helper for resolving relative image paths
  const getImageUrl = (images) => {
    if (images && images.length > 0 && images[0]) {
      const imgPath = images[0];
      return `${AppConfig?.api_media_url}/${imgPath}`;
    }
    return null;
  };

  // Convert Kobo (integer) to Naira string (divide by 100)
  const formatKoboToNaira = (amountInKobo) => {
    const nairaAmount = (Number(amountInKobo) || 0) / 100;
    return `${entity?.currency || "₦"}${nairaAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Auto-add selected product upon selection
  const handleProductSelect = (product) => {
    if (!product) return;

    // Determine price editable mode from sales_rule
    const isPriceOpen = salesRule?.price_model === "OPEN";

    setOrderItems((prevItems) => {
      const existingIndex = prevItems.findIndex(
        (item) => item.reference === product.reference
      );

      if (existingIndex > -1) {
        const updated = [...prevItems];
        updated[existingIndex].quantity += 1;
        return updated;
      } else {
        return [
          ...prevItems,
          {
            reference: product.reference,
            title: product.title,
            priceInKobo: Number(product.price) || 0,
            isOpenPrice: isPriceOpen,
            images: product.images || [],
            quantity: 1,
          },
        ];
      }
    });

    setSelectedProduct(null);
    setInputValue("");
  };

  // Custom Price update logic
  const handlePriceChange = (reference, newValue) => {
    const parsedValue = parseFloat(newValue);
    const newPriceInKobo = isNaN(parsedValue)
      ? 0
      : Math.round(parsedValue * 100);

    setOrderItems((prevItems) =>
      prevItems.map((item) =>
        item.reference === reference
          ? { ...item, priceInKobo: newPriceInKobo }
          : item
      )
    );
  };

  // Quantity updates
  const handleQuantityChange = (reference, newQty) => {
    const qty = Math.max(1, parseInt(newQty, 10) || 1);
    setOrderItems((prevItems) =>
      prevItems.map((item) =>
        item.reference === reference ? { ...item, quantity: qty } : item
      )
    );
  };

  // Remove single line item
  const handleRemoveItem = (reference) => {
    setOrderItems((prevItems) =>
      prevItems.filter((item) => item.reference !== reference)
    );
  };

  // Subtotal in Kobo before discount
  const subtotalInKobo = useMemo(() => {
    return orderItems.reduce(
      (acc, item) => acc + item.priceInKobo * item.quantity,
      0
    );
  }, [orderItems]);

  // Total discount calculation in Kobo
  const discountAmountInKobo = useMemo(() => {
    const discountRule = salesRule?.discount;
    if (!discountRule || !discountRule.discount || subtotalInKobo === 0) {
      return 0;
    }

    if (discountRule.is_percentage) {
      // Decimal rate (e.g. 0.10 for 10%): multiply subtotal directly by decimal
      return Math.round(subtotalInKobo * discountRule.discount);
    } else {
      // Flat discount is already provided in Kobo
      const flatKobo = Number(discountRule.discount) || 0;
      return Math.min(flatKobo, subtotalInKobo); // Cap discount at subtotal
    }
  }, [subtotalInKobo, salesRule]);

  // Final Grand Total in Kobo after applying discount
  const totalAmountInKobo = useMemo(() => {
    return Math.max(0, subtotalInKobo - discountAmountInKobo);
  }, [subtotalInKobo, discountAmountInKobo]);

  const totalItemCount = useMemo(() => {
    return orderItems.reduce((acc, item) => acc + item.quantity, 0);
  }, [orderItems]);

  async function handleSubmitOrder() {
    if (!applocation) {
      setSeverity("error");
      setErrMsg(
        "GPS coords missing. Grant browser location permissions and try again."
      );
      setOpen(true);
      return;
    }

    if (orderItems.length === 0) {
      setSeverity("warning");
      setErrMsg("Please add at least one product to the order.");
      setOpen(true);
      return;
    }

    const json = {
      reference: entityRef,
      rule: ruleRef,
      lat: applocation?.lat,
      lng: applocation?.lng,
      items: orderItems.map((item) => ({
        reference: item.reference,
        quantity: item.quantity,
        price: item.priceInKobo,
      })),
      payment_collection: collectPayment,
      payment_method: paymentMethod,
      fee_bearer: "MERCHANT",
    };

    setSubmitting(true);
    setShowLoading(true);

    await axios
      .post("/agents/ops/sales/submit.json", json)
      .then(async (result) => {
        setSubmitting(false);
        setShowLoading(false);
        if (result.data.status === "success") {
          console.log(result.data.data);
          if (result.data.data.result.charge_status === "pending") {
            navigate(
              `/ops/workspaces/entities/sales/payment/${result.data.data.reference}`
            );
            return;
          }
          await statusDialog.show(result.data.data.message, "success");
          setOrderItems([]);
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async () => {
        const err = `Unable to complete request. ${
          navigator.onLine
            ? ""
            : "Make sure you have an active internet connection"
        }`;
        setSubmitting(false);
        setShowLoading(false);
        await statusDialog.show(err, "error", "Failed");
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 2 }}
        open={showLoading || submitting}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox mt={1} mb={isMobile ? 10 : 5}>
        <MDBox ml={-2}>
          <Button
            onClick={() => window.history.go(-1)}
            startIcon={<Icon>west</Icon>}
            color="dark"
          >
            Back
          </Button>
        </MDBox>

        {pageLoaded && (
          <MDBox mt={1}>
            <Grid container spacing={3}>
              {/* Product Selection Panel */}
              <Grid item xs={12} md={7}>
                <Card sx={{ p: { xs: 2, sm: 3 } }}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Products (SKUs)
                  </MDTypography>

                  <Autocomplete
                    options={products}
                    getOptionLabel={(option) => option.title || ""}
                    value={selectedProduct}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                      setInputValue(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      setSelectedProduct(null);
                      setInputValue("");
                      handleProductSelect(newValue);
                    }}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      const imgUrl = getImageUrl(option.images);
                      return (
                        <li key={option.reference} {...optionProps}>
                          <MDBox
                            display="flex"
                            alignItems="center"
                            width="100%"
                            py={0.5}
                          >
                            <Avatar
                              src={imgUrl || ""}
                              alt={option.title}
                              variant="rounded"
                              sx={{ width: 40, height: 40, mr: 1.5 }}
                            >
                              {!imgUrl && <Icon>inventory_2</Icon>}
                            </Avatar>
                            <MDBox flex={1}>
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                              >
                                {option.title}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text"
                                display="block"
                              >
                                {formatKoboToNaira(option.price)}
                              </MDTypography>
                            </MDBox>
                            <Icon color="action" fontSize="small">
                              add_circle_outline
                            </Icon>
                          </MDBox>
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select product..."
                        variant="outlined"
                        fullWidth
                      />
                    )}
                  />

                  <Divider sx={{ my: 2 }} />

                  {/* Order Line Items List */}
                  <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
                    Selected Items ({orderItems.length})
                  </MDTypography>

                  {orderItems.length === 0 ? (
                    <MDBox
                      p={1}
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      bgColor="grey-100"
                      borderRadius="lg"
                    >
                      <MDTypography
                        variant="button"
                        color="text"
                        textAlign="center"
                      >
                        No products added yet. Select a product above to start.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox display="flex" flexDirection="column" gap={1.5}>
                      {orderItems.map((item) => {
                        const imgUrl = getImageUrl(item.images);
                        return (
                          <Paper
                            key={item.reference}
                            variant="outlined"
                            sx={{
                              p: { xs: 1.5, sm: 2 },
                              borderRadius: "10px",
                              borderColor: "#e0e0e0",
                            }}
                          >
                            <MDBox
                              display="flex"
                              alignItems="flex-start"
                              justifyContent="space-between"
                            >
                              <MDBox
                                display="flex"
                                alignItems="flex-start"
                                flex={1}
                                mr={1}
                              >
                                <Avatar
                                  src={imgUrl || ""}
                                  alt={item.title}
                                  variant="rounded"
                                  sx={{
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    mr: 1.5,
                                  }}
                                >
                                  {!imgUrl && <Icon>inventory_2</Icon>}
                                </Avatar>
                                <MDBox flex={1}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                      wordBreak: "break-word",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {item.title}
                                  </MDTypography>

                                  {item.isOpenPrice ? (
                                    <MDBox mt={0.5}>
                                      <MDInput
                                        type="number"
                                        fullWidth
                                        label="Price"
                                        value={
                                          item.priceInKobo > 0
                                            ? (
                                                item.priceInKobo / 100
                                              ).toString()
                                            : ""
                                        }
                                        onChange={(e) =>
                                          handlePriceChange(
                                            item.reference,
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        sx={{ mt: 1 }}
                                        inputProps={{ min: 0, step: "any" }}
                                        InputProps={{
                                          startAdornment: (
                                            <InputAdornment position="start">
                                              <MDTypography
                                                variant="h6"
                                                fontSize={14}
                                              >
                                                {entity?.currency}
                                              </MDTypography>
                                            </InputAdornment>
                                          ),
                                        }}
                                      />
                                    </MDBox>
                                  ) : (
                                    <MDTypography
                                      variant="caption"
                                      display="block"
                                      color="text"
                                    >
                                      Unit:{" "}
                                      {formatKoboToNaira(item.priceInKobo)}
                                    </MDTypography>
                                  )}
                                </MDBox>
                              </MDBox>

                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRemoveItem(item.reference)}
                              >
                                <Icon>delete</Icon>
                              </IconButton>
                            </MDBox>

                            <Divider sx={{ my: 1 }} />

                            <MDBox
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <MDBox display="flex" alignItems="center">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.reference,
                                      item.quantity - 1
                                    )
                                  }
                                >
                                  <Icon fontSize="medium">
                                    remove_circle_outline
                                  </Icon>
                                </IconButton>

                                <MDInput
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.reference,
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    min: 1,
                                    style: {
                                      textAlign: "center",
                                      width: "45px",
                                      padding: "4px",
                                    },
                                  }}
                                  size="small"
                                />

                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleQuantityChange(
                                      item.reference,
                                      item.quantity + 1
                                    )
                                  }
                                >
                                  <Icon fontSize="medium">
                                    add_circle_outline
                                  </Icon>
                                </IconButton>
                              </MDBox>

                              <MDBox textAlign="right">
                                <MDTypography
                                  variant="button"
                                  fontWeight="bold"
                                  color="dark"
                                >
                                  {formatKoboToNaira(
                                    item.priceInKobo * item.quantity
                                  )}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </Paper>
                        );
                      })}
                    </MDBox>
                  )}
                </Card>
              </Grid>

              {/* Summary and Checkout Panel */}
              <Grid item xs={12} md={5}>
                <Card sx={{ p: { xs: 2, sm: 3 } }}>
                  <MDTypography variant="h6" fontWeight="bold" mb={2}>
                    Order Summary
                  </MDTypography>

                  {entity && (
                    <MDBox mb={2} p={1} bgColor="grey-100" borderRadius="lg">
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                      >
                        {singularize(entity.relationship)}
                      </MDTypography>
                      <MDTypography variant="button" fontWeight="bold">
                        {entity.title}
                      </MDTypography>
                    </MDBox>
                  )}

                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                    px={1}
                  >
                    <MDTypography variant="button" color="text">
                      Total Units:
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="bold">
                      {totalItemCount} {totalItemCount === 1 ? "unit" : "units"}
                    </MDTypography>
                  </MDBox>

                  {/* Subtotal & Discount Breakdown */}
                  {salesRule?.discount && salesRule.discount.discount > 0 && (
                    <>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                        px={1}
                      >
                        <MDTypography variant="button" color="text">
                          Subtotal:
                        </MDTypography>
                        <MDTypography variant="button" fontWeight="bold">
                          {formatKoboToNaira(subtotalInKobo)}
                        </MDTypography>
                      </MDBox>

                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                        px={1}
                      >
                        <MDTypography variant="button" color="text">
                          Discount (
                          {salesRule.discount.is_percentage
                            ? `${salesRule.discount.discount * 100}%`
                            : formatKoboToNaira(salesRule.discount.discount)}
                          ):
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          fontWeight="bold"
                          color="error"
                        >
                          -{formatKoboToNaira(discountAmountInKobo)}
                        </MDTypography>
                      </MDBox>
                    </>
                  )}

                  {salesRule?.payment_required && (
                    <MDBox mb={2}>
                      <Divider sx={{ my: 2 }} />
                      <MDBox
                        mt={1}
                        pl={0}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDBox>
                          <MDTypography variant="h6">Payment</MDTypography>
                        </MDBox>
                        <Icon>payment</Icon>
                      </MDBox>

                      <MDBox mt={3} mb={0}>
                        <TextField
                          select
                          fullWidth
                          label="Payment Method"
                          variant="outlined"
                          value={paymentMethod}
                          onChange={(event) =>
                            setPaymentMethod(event.target.value)
                          }
                          SelectProps={{ style: { padding: "12px 0px" } }}
                        >
                          <MenuItem value="BANK_TRANSFER">
                            Bank Transfer
                          </MenuItem>
                        </TextField>
                      </MDBox>
                    </MDBox>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <MDBox
                    mt={1}
                    display="flex"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <MDTypography variant="h6" fontWeight="bold">
                      Total Amount:
                    </MDTypography>
                    <MDTypography
                      variant="h5"
                      color="success"
                      fontWeight="bold"
                    >
                      {formatKoboToNaira(totalAmountInKobo)}
                    </MDTypography>
                  </MDBox>

                  {!isMobile && (
                    <MDButton
                      variant="contained"
                      color="primary"
                      onClick={handleSubmitOrder}
                      fullWidth
                      disabled={orderItems.length === 0 || submitting}
                      startIcon={<Icon>payment</Icon>}
                    >
                      Place Order
                    </MDButton>
                  )}
                </Card>
              </Grid>
            </Grid>
          </MDBox>
        )}
      </MDBox>

      {/* Mobile Fixed Checkout Bar */}
      {isMobile && pageLoaded && (
        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            p: 2,
            borderRadius: "16px 16px 0 0",
            backgroundColor: "#fff",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <MDBox>
              <MDTypography variant="caption" color="text" display="block">
                Total
              </MDTypography>
              <MDTypography variant="h6" color="success" fontWeight="bold">
                {formatKoboToNaira(totalAmountInKobo)}
              </MDTypography>
            </MDBox>

            <MDButton
              variant="contained"
              color="primary"
              onClick={handleSubmitOrder}
              disabled={orderItems.length === 0 || submitting}
              startIcon={<Icon>payment</Icon>}
            >
              Place Order
            </MDButton>
          </MDBox>
        </Paper>
      )}

      <Footer />
    </DashboardLayout>
  );
}